import React, { useDeferredValue, useMemo, useState } from "react";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { OrgHomeScreenProps } from "../OrgHomeNavigation";
import { FormattedMessage } from "react-intl";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useSubscription,
} from "react-relay";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { WorkspaceSignalsList_workspace$key } from "./__generated__/WorkspaceSignalsList_workspace.graphql";
import { WorkspaceSignalsQuery } from "./__generated__/WorkspaceSignalsQuery.graphql";
import { OrgSearchInput } from "../OrgSearchInput";

import { FlatList } from "react-native";
import { useOrgListStyleProps } from "../useOrgHomeDetailContainerStyle";
import { AHEmptyScreen, AHSeparator, AHText, Box, useTheme } from "arkh";
import { WorkspaceSignalListItem } from "./WorkspaceSignalListItem";
import {
  WorkspaceSignalHeader,
  WorkspaceSignalSortKey,
} from "./WorkspaceSignalHeader";
import cuid from "cuid";

import { WorkspaceEmptyListItem } from "../WorkspaceEmptyListItem";
import { WorkspaceSignalsSubscription } from "./__generated__/WorkspaceSignalsSubscription.graphql";
import { Image } from "expo-image";
import { OrgButton } from "../OrgButton";
import {
  WorkspaceNavigationProp,
  WorkspaceSignalStack,
  WorkspaceSignalStackScreenProps,
} from "./WorkspaceSignalStack";
import { WorkspaceSignalsArchived } from "./WorkspaceSignalArchived/WorkspaceSignalsArchived";
import { useWorkspaceId, WorkspaceWritable } from "../workspaceId";
import { WorkspaceSignalDetailScreen } from "./WorkspaceSignalDetail/WorkspaceSignalDetail";
import { WorkspaceDetailNavigationHeader } from "../WorkspaceDetailNavigationHeader";

function sortWithFallback<T extends { title: string; id: string }>(
  fun: (a: T, b: T) => number
) {
  return (a: T, b: T) => {
    const result = fun(a, b);
    if (result !== 0) {
      return result;
    }
    const titleValue = b.title.localeCompare(a.title);
    if (titleValue !== 0) {
      return titleValue;
    }

    return b.id.localeCompare(a.id);
  };
}

function InnerWorkspaceSignals({
  fetchKey,

  ...props
}: {
  fetchKey: string;

  navigation: WorkspaceNavigationProp<"WorkspaceSignalsMain">;
}) {
  const workspaceId = useWorkspaceId();
  const data = useLazyLoadQuery<WorkspaceSignalsQuery>(
    graphql`
      query WorkspaceSignalsQuery($workspaceId: ID!) {
        workspace(id: $workspaceId) {
          id
          ...WorkspaceSignalsList_workspace
        }
      }
    `,
    {
      workspaceId,
    },
    { fetchKey, fetchPolicy: "store-and-network" }
  );

  return data.workspace ? (
    <List
      workspace={data.workspace}
      openSignalWorkspaceEdit={(id) => {
        props.navigation.navigateDeprecated("WorkspaceSignalEdit", {
          workspaceId: data.workspace.id,
          id,
        });
      }}
      openSignalWorkspace={(id) => {
        props.navigation.navigateDeprecated("WorkspaceSignalDetail", {
          workspaceId: data.workspace.id,
          signalWorkspaceId: id,
        });
      }}
    />
  ) : null;
}

function WorkspaceSignalsTitle() {
  const intl_tokens = useIntlTokens();
  return intl_tokens.Signals;
}

function WorkspaceSignalsSubTitle() {
  return (
    <FormattedMessage
      description="WorkspaceSignals subtitle"
      defaultMessage="Send important or urgent messages to any unit in your workspace using a Signal. Track who sees your messages in real time. Urgent signals are pinned at the top of the app’s home page and include push notifications to alert recipients."
    />
  );
}
function List(props: {
  workspace: WorkspaceSignalsList_workspace$key;
  openSignalWorkspace: (signalId: string) => void;
  openSignalWorkspaceEdit: (signalId: string) => void;
}) {
  const [_search, setSearch] = React.useState("");
  const search = useDeferredValue(_search);

  const workspace = useFragment(
    graphql`
      fragment WorkspaceSignalsList_workspace on Workspace {
        id
        topics {
          __typename
        }
        activeSignals {
          id
          title
          body

          topic {
            id
            name
          }
          createdAtDate {
            isoString
          }
          publishedAtDate {
            isoString
          }
          expireAtDate {
            isoString
          }

          status
          signalCount

          priorityOrder
          ...WorkspaceSignalListItem_signal
        }
      }
    `,
    props.workspace
  );

  useSubscription<WorkspaceSignalsSubscription>(
    useMemo(
      () => ({
        variables: {
          workspaceId: workspace.id,
        },
        subscription: graphql`
          subscription WorkspaceSignalsSubscription($workspaceId: ID!) {
            signalWorkspaceChanged(workspaceId: $workspaceId) {
              id
              title
              body
              createdAtDate {
                isoString
              }
              publishedAtDate {
                isoString
              }
              expireAtDate {
                isoString
              }
              status
              signalCount

              priorityOrder
              ...WorkspaceSignalListItem_signal
            }
          }
        `,
      }),
      [workspace.id]
    )
  );

  const listStyle = useOrgListStyleProps();
  const filteredSignals = useMemo(() => {
    if (search.length > 0) {
      const s = search.toLocaleLowerCase();
      return workspace.activeSignals.filter(
        (item) =>
          item?.id &&
          (item.title.toLocaleLowerCase().includes(s) ||
            item.body?.toLowerCase().includes(s))
      );
    }
    return workspace.activeSignals.filter((item) => item?.id);
  }, [search, workspace.activeSignals]);

  const [order, setOrder] = useState<{
    column: WorkspaceSignalSortKey;
    order: "ASC" | "DESC";
  }>({
    column: "published",
    order: "DESC",
  });

  type Signal = (typeof workspace.activeSignals)[number];

  const sortFunction = useMemo(() => {
    switch (order.column) {
      case "priority":
        return (a: Signal, b: Signal) => {
          if (a.priorityOrder === b.priorityOrder) {
            return b.title.localeCompare(a.title);
          }
          return a.priorityOrder - b.priorityOrder;
        };
      case "description":
        return (a: Signal, b: Signal) => b.title.localeCompare(a.title);

      case "published":
        return (a: Signal, b: Signal) => {
          if (a.publishedAtDate?.isoString === b.publishedAtDate?.isoString) {
            if (a.createdAtDate?.isoString === b.createdAtDate?.isoString) {
              return a.title.localeCompare(b.title);
            }
            return a.createdAtDate.isoString.localeCompare(
              b.createdAtDate.isoString
            );
          }
          if (a.publishedAtDate == null) {
            return 1;
          }
          if (b.publishedAtDate == null) {
            return -1;
          }
          return a.publishedAtDate.isoString.localeCompare(
            b.publishedAtDate.isoString
          );
        };
      case "filter":
        return (a: Signal, b: Signal) => b.status.localeCompare(a.status);
      case "topic":
        return (a: Signal, b: Signal) => {
          // Handle cases where either topic is undefined
          if (!a.topic && !b.topic) return a.title.localeCompare(b.title);
          if (!a.topic) return 1; // Items without topics go last
          if (!b.topic) return -1; // Items without topics go last

          if (a.topic?.id === b.topic?.id) {
            return a.title.localeCompare(b.title);
          }
          return a.topic.name.localeCompare(b.topic?.name || "");
        };
    }
  }, [order.column]);

  const showTopics = workspace.topics ? workspace.topics.length > 0 : false;

  const signals = useMemo(() => {
    const sf = sortWithFallback(sortFunction);
    if (order.order === "DESC") {
      return filteredSignals.slice().sort((a, b) => -sf(a, b));
    }

    return filteredSignals.slice().sort(sf);
  }, [filteredSignals, order.order, sortFunction]);

  if (workspace.activeSignals.length === 0) {
    return (
      <AHEmptyScreen
        title={<WorkspaceSignalsTitle />}
        subtitle={<WorkspaceSignalsSubTitle />}
        illustration={
          <Box style={{ flex: 1 }}>
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={require("./signals.png")}
            />
          </Box>
        }
      />
    );
  }

  return (
    <FlatList
      {...listStyle}
      data={signals}
      keyboardShouldPersistTaps="handled"
      renderItem={({ item, index }) => {
        return (
          <WorkspaceSignalListItem
            workspaceSignal={item}
            isLast={index + 1 === signals.length}
            onPress={props.openSignalWorkspace}
            onPressEdit={props.openSignalWorkspaceEdit}
            showTopics={showTopics}
          />
        );
      }}
      ListHeaderComponent={
        <Box color="surface-background-light" spaceTop="medium">
          <OrgSearchInput value={_search} onChangeText={setSearch} />
          <WorkspaceSignalHeader
            order={order}
            onOrderChange={setOrder}
            showTopics={showTopics}
          />
          <AHSeparator />
        </Box>
      }
      ListEmptyComponent={WorkspaceEmptyListItem}
      ItemSeparatorComponent={AHSeparator}
      stickyHeaderIndices={[0]}
    />
  );
}

function WorkspaceSignalsMainScreen(
  props: WorkspaceSignalStackScreenProps<"WorkspaceSignalsMain">
) {
  const intl_tokens = useIntlTokens();
  const workspaceId = useWorkspaceId();
  return (
    <>
      <WorkspaceDetailNavigationHeader
        left={
          <AHText type="subtitle">
            <WorkspaceSignalsTitle />
          </AHText>
        }
        right={
          <Box style={{ flexDirection: "row", alignItems: "center" }}>
            <OrgButton
              backgroundColor="transparent"
              icon="archive"
              tintColor="text-secondary"
              title={intl_tokens.ShowArchived}
              onPress={() => {
                props.navigation.navigateDeprecated(
                  "WorkspaceSignalsArchived",
                  {
                    workspaceId,
                  }
                );
              }}
            />
            <Box space="medium" />
            <WorkspaceWritable>
              <OrgButton
                backgroundColor="primary"
                tintColor="text-inverted"
                title={intl_tokens.Add}
                icon="add"
                onPress={() => {
                  props.navigation.navigateDeprecated("WorkspaceSignalEdit", {
                    workspaceId,
                    id: cuid(),
                    create: true,
                  });
                }}
              />
            </WorkspaceWritable>
          </Box>
        }
      />
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => <InnerWorkspaceSignals {...props} fetchKey={fetchKey} />}
      </LazyQueryNetworkBoundrary>
    </>
  );
}

export function WorkspaceSignalsScreen(
  props: OrgHomeScreenProps<"WorkspaceSignals">
) {
  const theme = useTheme();
  return (
    <WorkspaceSignalStack.Navigator
      screenOptions={{
        headerStyle: {
          backgroundColor: theme["surface-background-light"],
        },
        cardStyle: {
          backgroundColor: theme["surface-background-light"],
        },
      }}
    >
      <WorkspaceSignalStack.Screen
        name="WorkspaceSignalsMain"
        component={WorkspaceSignalsMainScreen}
        options={{ headerShown: false }}
      />
      <WorkspaceSignalStack.Screen
        name="WorkspaceSignalDetail"
        component={WorkspaceSignalDetailScreen}
        options={{
          headerShown: false,
        }}
      />
      <WorkspaceSignalStack.Screen
        name="WorkspaceSignalsArchived"
        component={WorkspaceSignalsArchived}
        options={{
          headerShown: false,
        }}
      />
    </WorkspaceSignalStack.Navigator>
  );
}
